import { useEffect, useState } from 'react';
import { API_BASE } from '../client/config';

// =============================================================================
// 콘텐츠 사용처 — 지우기 전에 어디에 걸려 있는지 보여 준다 (2026-09-17 대교)
//
// 예전에는 삭제를 누른 뒤에야 서버가 409로 알려 줘서, "삭제하시겠습니까?"를 보고 눌렀는데
// 그제서야 "안 된다"가 나왔다. 확인 모달이 열릴 때 미리 물어보고, 쓰이는 곳이 있으면 목록으로
// 보여 주면서 삭제 버튼을 막는다.
//
// 판정은 서버의 findContentUsage 하나를 그대로 쓴다(GET …/:id/usage). 화면이 따로 계산하면
// 목록에 안 보이는 스케줄·다른 그룹 콘텐츠를 놓쳐 서버와 다른 말을 하게 된다.
// =============================================================================

export type ContentUsage = {
  kind: 'playlist' | 'schedule' | 'notice' | 'device'; name: string;
  /** 날짜 구간 배정이면 그 편성 id·시작일 — "스케줄에서 보기"로 곧장 연다(SCDS-62). */
  assignmentId?: string; date?: string;
};

const GROUPS: Array<{ kind: ContentUsage['kind']; label: string }> = [
  { kind: 'playlist', label: '플레이리스트' },
  { kind: 'notice', label: '슬라이드' },
  { kind: 'schedule', label: '스케줄' },
  { kind: 'device', label: '기기 대기 화면' },
];

/** 열려 있는 동안 한 번 물어본다. id 가 null 이면(모달이 닫힘) 아무것도 하지 않는다. */
export function useContentUsage(kind: 'media' | 'notice' | 'playlist', id: string | null): {
  usage: ContentUsage[] | null;
  loading: boolean;
} {
  const [usage, setUsage] = useState<ContentUsage[] | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!id) { setUsage(null); setLoading(false); return; }
    const controller = new AbortController();
    setUsage(null); setLoading(true);
    const path = kind === 'media' ? 'media' : kind === 'playlist' ? 'playlists' : 'notices';
    fetch(`${API_BASE}/api/${path}/${encodeURIComponent(id)}/usage`, { credentials: 'include', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json() as { usage?: ContentUsage[] };
        if (!controller.signal.aborted) setUsage(body.usage ?? []);
      })
      // 조회가 실패해도 삭제를 막지는 않는다 — 서버가 지울 때 한 번 더 본다.
      .catch(() => { if (!controller.signal.aborted) setUsage([]); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [kind, id]);
  return { usage, loading };
}

/** 여러 개를 한 번에 지우기 전에 각각의 사용처를 묻는다(SCDS-61). 조회가 실패한 항목은 비어 있는 것으로 본다 —
 * 서버가 지울 때 한 번 더 막는다. */
export function useBulkContentUsage(kind: 'media' | 'notice' | 'playlist', ids: readonly string[] | null): {
  usage: Map<string, ContentUsage[]> | null;
  loading: boolean;
} {
  const [usage, setUsage] = useState<Map<string, ContentUsage[]> | null>(null);
  const key = ids ? ids.join(',') : '';
  useEffect(() => {
    if (!ids || !ids.length) { setUsage(null); return; }
    const controller = new AbortController();
    setUsage(null);
    const path = kind === 'media' ? 'media' : kind === 'playlist' ? 'playlists' : 'notices';
    void Promise.all(ids.map(async (id) => {
      try {
        const response = await fetch(`${API_BASE}/api/${path}/${encodeURIComponent(id)}/usage`, { credentials: 'include', signal: controller.signal });
        if (!response.ok) return [id, []] as const;
        const body = await response.json() as { usage?: ContentUsage[] };
        return [id, body.usage ?? []] as const;
      } catch {
        return [id, []] as const;
      }
    })).then((rows) => { if (!controller.signal.aborted) setUsage(new Map(rows.map(([id, list]) => [id, [...list]]))); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key]);
  return { usage, loading: !!ids?.length && usage === null };
}

export function ContentUsageNotice({ usage, noun }: { usage: ContentUsage[]; noun: string }): JSX.Element {
  return (
    <div className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2.5 text-[13px] text-red">
      <p className="font-medium">이 {noun}를 쓰고 있어 지울 수 없습니다.</p>
      <div className="mt-2 flex flex-col gap-2">
        {GROUPS.map(({ kind, label }) => {
          const entries = usage.filter((entry) => entry.kind === kind);
          if (!entries.length) return null;
          return (
            <div key={kind}>
              <span className="font-medium">{label} {entries.length}</span>
              <ul className="mt-1 flex flex-col gap-0.5 pl-3">
                {entries.map((entry) => <li key={entry.assignmentId ?? entry.name} className="flex min-w-0 items-center gap-2">
                  <span className="truncate">· {entry.name}{entry.date ? ` (${entry.date} 시작)` : ''}</span>
                  {/* 어디 있는지 몰라 달력을 뒤지지 않게 그 스케줄을 바로 연다(SCDS-62). */}
                  {entry.assignmentId && <a className="shrink-0 text-accent underline underline-offset-2"
                    href={`#/schedule?assignment=${encodeURIComponent(entry.assignmentId)}${entry.date ? `&date=${entry.date}` : ''}`}>
                    스케줄에서 보기</a>}
                </li>)}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-text-dim">먼저 그곳에서 빼면 지울 수 있습니다.</p>
    </div>
  );
}
